import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getUserProfile } from '../../Redux/userSlice';
import Navbar from '../../components/Navbar';
import { FaArrowRight, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import campaignApplicationService from '../../company/services/campaignApplicationService';
import campaignAdvertisingService from '../../company/services/campaignAdvertisingService';
import countries from '../../data/countries.json';

const CampaignSubmissionForm = () => {
  const { id } = useParams(); // campaignAdvertisingId from URL
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const { loading: userLoading } = useSelector((state) => state.user);

  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [campaignCurrency, setCampaignCurrency] = useState({
    code: 'INR',
    symbol: '₹'
  });

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Initialize form data with user profile data
  const [formData, setFormData] = useState({
    // Compulsory fields
    name: '',
    contactNumber: '',
    email: '',
    instagramProfileLink: '',
    instagramUsername: '',
    insight: '',
    proposedRate: 0,
    location: {
      city: '',
      pinCode: '',
      country: '',
      state: ''
    },
    category: [],
    gender: '',
    // Optional fields
    language: [],
    age: null,
    height: '',
    address: '',
    agencyName: ''
  });

  // Fetch user profile on mount
  useEffect(() => {
    if (!user) {
      dispatch(getUserProfile());
    }
  }, [dispatch, user]);

  // Auto-fill form when user data is available
  useEffect(() => {
    if (user) {
      const instagramUrl = user.username
        ? `https://www.instagram.com/${user.username}/`
        : '';

      setFormData(prev => ({
        ...prev,
        name: user.name || user.username || '',
        contactNumber: user.mobile || '',
        email: user.email || '',
        instagramProfileLink: instagramUrl,
        instagramUsername: user.username || '',
        location: {
          city: prev.location.city || '',
          pinCode: user.postalCode || '',
          country: user.country || '',
          state: user.state || '',
        },
        category: user.contentType || [],
        gender: user.gender || '',
        language: prev.language || [],
        age: user.dob ? calculateAge(user.dob) : null,
        followerCount: user.instagramFollowers || 0,
        followingCount: user.instagramFollowing || 0,
        mediaCount: user.instagramMediaCount || 0,
        profileImage: user.profileImage?.url || user.instagramProfilePicture || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchCampaignCurrency = async () => {
      try {
        const response = await campaignAdvertisingService.getPublishedCampaignAdvertisings();

        if (response?.success && Array.isArray(response.data)) {
          const matched = response.data.find(item => item.id === id);
          const currency = matched?.campaign?.currency || matched?.currency;

          if (currency) {
            setCampaignCurrency({
              code: (currency.code || 'INR').toUpperCase(),
              symbol: currency.symbol || '₹'
            });
          }
        }
      } catch (err) {
        console.error('Failed to load campaign currency', err);
      }
    };

    fetchCampaignCurrency();
  }, [id]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAttachmentError('');

    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'application/pdf',
    ];

    if (!allowedTypes.includes(file.type)) {
      setAttachmentError('Only JPG, PNG images or PDF files are allowed');
      e.target.value = '';
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSize) {
      setAttachmentError('File size must be less than 5 MB');
      e.target.value = '';
      return;
    }

    setAttachment(file);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          [locationField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCategoryChange = (category) => {
    setFormData(prev => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter(c => c !== category)
        : [...prev.category, category]
    }));
  };

  const handleLanguageChange = (language) => {
    setFormData(prev => ({
      ...prev,
      language: prev.language.includes(language)
        ? prev.language.filter(l => l !== language)
        : [...prev.language, language]
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.contactNumber.trim()) {
      setError('Contact/WhatsApp number is required');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Valid email is required');
      return false;
    }
    if (!formData.instagramProfileLink.trim()) {
      setError('Instagram profile link is required');
      return false;
    }
    // if (!formData.insight.trim()) {
    //   setError('Insight is required');
    //   return false;
    // }
    if (!formData.location.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!formData.location.pinCode.trim()) {
      setError('Pin code is required');
      return false;
    }
    if (!formData.location.country.trim()) {
      setError('Country is required');
      return false;
    }
    if (!formData.category.length) {
      setError('At least one category is required');
      return false;
    }
    if (!formData.gender.trim()) {
      setError('Gender is required');
      return false;
    }
    return true;
  };
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setError('');
  //   setSuccess(false);

  //   if (!validateForm()) {
  //     return;
  //   }

  //   setSubmitting(true);

  //   try {
  //     const payload = {
  //       name: formData.name.trim(),
  //       contactNumber: formData.contactNumber.trim(),
  //       email: formData.email.trim(),
  //       instagramProfileLink: formData.instagramProfileLink.trim(),
  //       instagramUsername: formData.instagramUsername.trim(),
  //       insight: formData.insight.trim(),
  //       location: {
  //         city: formData.location.city.trim(),
  //         pinCode: formData.location.pinCode.trim(),
  //         country: formData.location.country.trim(),
  //         state: formData.location.state.trim(),
  //       },
  //       category: formData.category,
  //       gender: formData.gender,
  //       language: formData.language,
  //       age: formData.age || undefined,
  //       height: formData.height.trim() || undefined,
  //       address: formData.address.trim() || undefined,
  //       agencyName: formData.agencyName.trim() || undefined,
  //       followerCount: formData.followerCount || 0,
  //       followingCount: formData.followingCount || 0,
  //       mediaCount: formData.mediaCount || 0,
  //       profileImage: formData.profileImage || undefined,
  //       instagramInsights: user.instagramAverages || {}
  //     };

  //     const response = await campaignApplicationService.submitApplication(id, payload);

  //     if (response.success) {
  //       setSuccess(true);
  //       toast.success('Application submitted successfully!');
  //       setTimeout(() => {
  //         navigate('/campaign');
  //       }, 2000);
  //     } else {
  //       setError(response.message || 'Failed to submit application');
  //       toast.error(response.message || 'Failed to submit application');
  //     }
  //   } catch (err) {
  //     const errorMessage = err.message || 'Failed to submit application. Please try again.';
  //     setError(errorMessage);
  //     toast.error(errorMessage);
  //   } finally {
  //     setSubmitting(false);
  //   }
  // };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setError('');
  //   setSuccess(false);

  //   if (!validateForm()) return;

  //   setSubmitting(true);

  //   try {
  //     const payload = {
  //       name: formData.name.trim(),
  //       contactNumber: formData.contactNumber.trim(),
  //       email: formData.email.trim(),
  //       instagramProfileLink: formData.instagramProfileLink.trim(),
  //       instagramUsername: formData.instagramUsername.trim(),
  //       insight: formData.insight.trim(),
  //       location: {
  //         city: formData.location.city.trim(),
  //         pinCode: formData.location.pinCode.trim(),
  //         country: formData.location.country.trim(),
  //         state: formData.location.state.trim(),
  //       },
  //       category: formData.category,
  //       gender: formData.gender,
  //       language: formData.language,
  //       age: formData.age || undefined,
  //       height: formData.height.trim() || undefined,
  //       address: formData.address.trim() || undefined,
  //       agencyName: formData.agencyName.trim() || undefined,
  //       followerCount: formData.followerCount || 0,
  //       followingCount: formData.followingCount || 0,
  //       mediaCount: formData.mediaCount || 0,
  //       profileImage: formData.profileImage || undefined,
  //       instagramInsights: user.instagramAverages || {}
  //     };

  //     const formDataPayload = new FormData();
  //     // Append normal fields
  //     Object.entries(payload).forEach(([key, value]) => {
  //       if (value !== undefined && value !== null) {
  //         if (typeof value === 'object' && !(value instanceof File)) {
  //           formDataPayload.append(key, JSON.stringify(value));
  //         } else {
  //           formDataPayload.append(key, value);
  //         }
  //       }
  //     });

  //     // Append attachment
  //     if (attachment) {
  //       formDataPayload.append('attachment', attachment);
  //     }

  //     const response = await campaignApplicationService.submitApplication(
  //       id,
  //       formDataPayload
  //     );

  //     console.log('[SUBMIT] Raw response:', response);

  //     // ✅ STRICT SUCCESS CHECK
  //     if (response?.success === true || response?.success === 'true') {
  //       console.log('[SUBMIT] Success confirmed');

  //       toast.success(
  //         response.message || 'Application submitted successfully'
  //       );

  //       // IMPORTANT: exit function immediately
  //       navigate('/campaign', { replace: true });
  //       return;
  //     }
  //     // ❌ backend explicitly returned success=false
  //     console.error('[SUBMIT] Backend failure:', response);
  //     toast.error(
  //       response?.message || 'Application submission failed'
  //     );
  //   } catch (err) {
  //     const message =
  //       err?.response?.data?.message ||
  //       err?.message ||
  //       'Network error. Please try again.';

  //     toast.error(message);
  //   }
  //   finally {
  //     setSubmitting(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
  
    if (!validateForm()) return;
  
    setSubmitting(true);
  
    try {
      const proposedRateAmount = Math.max(0, Number(formData.proposedRate ?? 0));
      const normalizedCurrency = {
        code: (campaignCurrency.code || 'INR').toUpperCase(),
        symbol: campaignCurrency.symbol || '₹'
      };
      const proposedRate = {
        amount: Number.isFinite(proposedRateAmount) ? proposedRateAmount : 0,
        currency: normalizedCurrency
      };

      // 1️⃣ Build payload
      const payload = {
        name: formData.name.trim(),
        contactNumber: formData.contactNumber.trim(),
        email: formData.email.trim(),
        instagramProfileLink: formData.instagramProfileLink.trim(),
        instagramUsername: formData.instagramUsername.trim(),
        insight: formData.insight.trim(),
        proposedRate,
        location: {
          city: formData.location.city.trim(),
          pinCode: formData.location.pinCode.trim(),
          country: formData.location.country.trim(),
          state: formData.location.state.trim(),
        },
        category: formData.category,
        gender: formData.gender,
        language: formData.language,
        age: formData.age || undefined,
        height: formData.height.trim() || undefined,
        address: formData.address.trim() || undefined,
        agencyName: formData.agencyName.trim() || undefined,
        followerCount: formData.followerCount || 0,
        followingCount: formData.followingCount || 0,
        mediaCount: formData.mediaCount || 0,
        profileImage: formData.profileImage || undefined,
        instagramInsights: user.instagramAverages || {}
      };
  
      // 2️⃣ Convert to FormData
      const formDataPayload = new FormData();
  
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === 'object' && !(value instanceof File)) {
            formDataPayload.append(key, JSON.stringify(value));
          } else {
            formDataPayload.append(key, value);
          }
        }
      });
  
      if (attachment) {
        formDataPayload.append('attachment', attachment);
      }
  
      // 3️⃣ Submit application
      const res = await campaignApplicationService.submitApplication(
        id,
        formDataPayload
      );
  
      console.log('[SUBMIT] Raw response:', res);
  
      /**
       * 4️⃣ BULLETPROOF SUCCESS CHECK
       * - Trust HTTP status first
       * - Then normalize success flag
       */
      // const isHttpSuccess = res.status >= 200 && res.status < 300;
  
      // const apiSuccess = Boolean(
      //   res?.data &&
      //   (
      //     res.data.success === true ||
      //     res.data.success === 'true' ||
      //     res.data.success === 1 ||
      //     res.data.success === '1' ||
      //     String(res.data.success).toLowerCase() === 'true'
      //   )
      // );
  
      // if (isHttpSuccess && apiSuccess) {
      //   toast.success(
      //     res.data?.message || 'Application submitted successfully'
      //   );
  
      //   navigate('/campaign', { replace: true });
      //   return;
      // }
  
      // // 5️⃣ Explicit backend failure
      // console.error('[SUBMIT] Backend failure:', res.data);
      // toast.error(
      //   res.data?.message || 'Application submission failed'
      // );

      // ✅ SUCCESS → NO CHECK → DIRECT ACTION
    toast.success('Application submitted successfully');
    navigate('/campaign', { replace: true });

  
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Network error. Please try again.';
  
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };
  

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Navbar />
        <div className="text-center">
          <FaSpinner className="animate-spin text-orange-500 text-4xl mx-auto mb-4" />
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  const contentCategories = [
    'fashion', 'travel', 'sports', 'entertainment', 'technology',
    'vlogger', 'musician', 'photography', 'health and fitness',
    'home decor', 'beauty and personal care'
  ];

  const languages = ['English', 'Hindi', 'Tamil', 'Telugu', 'Bengali', 'Marathi', 'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Other'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Campaign Application Form
          </h1>
          <p className="text-gray-600 mb-6">
            Please review and complete your application details. Fields marked with <span className="text-red-500">*</span> are required.
          </p>

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <FaCheckCircle className="text-green-500 text-xl" />
              <p className="text-green-800">Application submitted successfully! Redirecting...</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Compulsory Fields Section */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Required Information</h2>

              {/* Name */}
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Contact Number */}
              <div className="mb-4">
                <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact/WhatsApp Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Instagram Profile Link */}
              <div className="mb-4">
                <label htmlFor="instagramProfileLink" className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram Profile Link <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  id="instagramProfileLink"
                  name="instagramProfileLink"
                  value={formData.instagramProfileLink}
                  onChange={handleChange}
                  required
                  placeholder="https://www.instagram.com/username/"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Instagram Username */}
              <div className="mb-4">
                <label htmlFor="instagramUsername" className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram Username
                </label>
                <input
                  type="text"
                  id="instagramUsername"
                  name="instagramUsername"
                  value={formData.instagramUsername}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Proposed Rate */}
              <div className="mb-4">
                <label htmlFor="proposedRate" className="block text-sm font-medium text-gray-700 mb-1">
                  Enter your proposed rate for this campaign ({campaignCurrency.code || 'INR'})
                </label>
                <div className="relative">
                  {campaignCurrency.symbol && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {campaignCurrency.symbol}
                    </span>
                  )}
                  <input
                    type="number"
                    id="proposedRate"
                    name="proposedRate"
                    value={formData.proposedRate}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    placeholder="0"
                    onWheel={(e) => e.target.blur()}
                    className={`w-full py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${campaignCurrency.symbol ? 'pl-10 pr-4' : 'px-4'}`}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Leave empty to submit as 0.</p>
              </div>

              {/* Instagram Insights */}



              {/* Location */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="location.city" className="block text-xs text-gray-600 mb-1">City</label>
                    <input
                      type="text"
                      id="location.city"
                      name="location.city"
                      value={formData.location.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="location.pinCode" className="block text-xs text-gray-600 mb-1">Pin Code</label>
                    <input
                      type="text"
                      id="location.pinCode"
                      name="location.pinCode"
                      value={formData.location.pinCode}
                      onChange={handleChange}
                      required
                      maxLength="6"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="location.country" className="block text-xs text-gray-600 mb-1">Country</label>
                    <select
                      id="location.country"
                      name="location.country"
                      value={formData.location.country}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Category */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {contentCategories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => handleCategoryChange(cat)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${formData.category.includes(cat)
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                {formData.category.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Please select at least one category</p>
                )}
              </div>

              {/* Gender */}
              <div className="mb-4">
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Optional Fields Section */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Optional Information</h2>

              {/* Language */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => handleLanguageChange(lang)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${formData.language.includes(lang)
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              {/* Attachment */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Insights Image or PDF (max 5 MB)
                </label>

                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />

                {attachment && (
                  <p className="text-xs text-gray-600 mt-1">
                    Selected file: <strong>{attachment.name}</strong>
                  </p>
                )}

                {attachmentError && (
                  <p className="text-xs text-red-500 mt-1">{attachmentError}</p>
                )}
              </div>

                {/* Insight */}
                <div className="mb-4">
                <label htmlFor="insight" className="block text-sm font-medium text-gray-700 mb-1">
                  Insight 
                </label>
                <textarea
                  id="insight"
                  name="insight"
                  value={formData.insight}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about your content, audience, and why you're a good fit..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>


              {/* Age */}
              <div className="mb-4">
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                  Age
                </label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age || ''}
                  onChange={handleChange}
                  min="0"
                  max="150"
                  onWheel={(e) => e.target.blur()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Height */}
              <div className="mb-4">
                <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-1">
                  Height
                </label>
                <input
                  type="text"
                  id="height"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  placeholder="e.g., 5'8&quot; or 173 cm"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Address (for barter only) */}
              <div className="mb-4">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Address (for barter only)
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Agency Name */}
              <div className="mb-4">
                <label htmlFor="agencyName" className="block text-sm font-medium text-gray-700 mb-1">
                  Agency Name
                </label>
                <input
                  type="text"
                  id="agencyName"
                  name="agencyName"
                  value={formData.agencyName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || success}
                className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <FaArrowRight />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CampaignSubmissionForm;
